import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/apiClient';
import {
  Button,
  TextField,
  Container,
  Typography,
  IconButton,
  Alert,
  useTheme,
  Menu as MuiMenu,
  MenuItem
} from '@mui/material';
import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import LanguageIcon from '@mui/icons-material/Language';
import { ThemeContext } from '../context/ThemeContext';
import { useNotifications } from '../context/NotificationContext';
import NotificationContainer from '../components/NotificationContainer';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '../utils/errorMessage';
import { AUTH_API } from '../utils/apiConfig';

const Recovery = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toggleTheme } = useContext(ThemeContext);
  const { showError, showSuccess } = useNotifications();
  const { t, i18n } = useTranslation('auth');
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const theme = useTheme();

  // Получаем параметры из URL
  const email = searchParams.get('mail');
  const token = searchParams.get('token');

  useEffect(() => {
    // Проверяем наличие обязательных параметров и показываем предупреждение
    if (!email || !token) {
      showError(t('missingParams'));
    }
  }, [email, token, showError]);

  const validateForm = () => {
    // Проверяем наличие обязательных параметров
    if (!hasRequiredParams) {
      showError(t('missingParamsRecovery'));
      return false;
    }

    const newErrors = {};

    if (!password) {
      newErrors.password = t('passwordRequiredError');
    } else if (password.length < 6) {
      newErrors.password = t('passwordMinLength');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('confirmPasswordError');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('passwordsMismatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(`${AUTH_API}/v1/auth/password/reset`, {
        email,
        token,
        password
      });
      
      showSuccess(t('passwordChanged'));
      navigate('/login');
    } catch (error) {
      showError(t('passwordChangeError', { message: getErrorMessage(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'password') {
      setPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Проверяем наличие параметров для отображения предупреждения
  const hasRequiredParams = email && token;

  return (
    <Container style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%', maxWidth: 'none' }}>
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: '4px' }}>
        <IconButton onClick={(e) => setLangAnchorEl(e.currentTarget)}>
          <LanguageIcon />
        </IconButton>
        <MuiMenu anchorEl={langAnchorEl} open={Boolean(langAnchorEl)} onClose={() => setLangAnchorEl(null)}>
          {[{ code: 'ru', label: 'RU' }, { code: 'en', label: 'EN' }, { code: 'zh', label: 'ZH' }].map((lang) => (
            <MenuItem key={lang.code} selected={i18n.language === lang.code} onClick={() => { i18n.changeLanguage(lang.code); setLangAnchorEl(null); }}>
              {lang.label}
            </MenuItem>
          ))}
        </MuiMenu>
        <IconButton onClick={toggleTheme}>
          <Brightness4OutlinedIcon />
        </IconButton>
      </div>
      
      <Container maxWidth="sm" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 30px', borderRadius: '10px', backgroundColor: theme.palette.background.paper }}>
        <Typography variant="h5" gutterBottom align="center" style={{ color: theme.palette.text.primary }}>
          {t('recoveryTitle')}
        </Typography>

        {!hasRequiredParams && (
          <Alert severity="warning" style={{ width: '100%', marginBottom: '20px' }}>
            {t('missingParamsRecovery')}
          </Alert>
        )}

        {hasRequiredParams ? (
          <Typography variant="body1" gutterBottom align="center" style={{ marginBottom: '30px', color: theme.palette.text.primary }}>
            {t('enterNewPassword')}
            <Typography component="span" variant="body1" color="primary" fontWeight="bold">
              {email}
            </Typography>
          </Typography>
        ) : (
          <Typography variant="body1" gutterBottom align="center" style={{ marginBottom: '30px', color: theme.palette.text.primary }}>
            {t('paramsRequiredUrl')}
          </Typography>
        )}

        <TextField
          label={t('newPasswordLabel')}
          type="password"
          variant="outlined"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          error={!!errors.password}
          helperText={errors.password || t('passwordHint')}
        />

        <TextField
          label={t('confirmPasswordLabel')}
          type="password"
          variant="outlined"
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />

        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          onClick={handlePasswordChange}
          disabled={isLoading}
          style={{ marginTop: '20px' }}
        >
          {isLoading ? t('changingPassword') : t('changePasswordButton')}
        </Button>

        <Button 
          variant="text" 
          color="primary" 
          onClick={() => navigate('/login')}
          style={{ marginTop: '10px' }}
        >
          {t('backToLogin')}
        </Button>
      </Container>
      
      <NotificationContainer />
    </Container>
  );
};

export default Recovery;
